import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSites } from "../../services/sites";
import { openSettingsSection } from "../../utils/settingsLink";
import { supportLink, supportMessage } from "../../utils/support";
import { useOwnezzyContact } from "../../utils/branding";
import FloatingPanel from "../../components/FloatingPanel";

const COLLAPSED_KEY = "oz_admin_guide_collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

// "How to" guide at the top of the panel for store owners: what they can do and the exact link for each.
// Open by default; closing it is remembered on this device. On Overview it is in the page; on other tabs (floating)
// it is an "i" button that opens it, always unfolded (FloatingPanel).
export default function StoreGuide({ user, setTab, floating = false, open = false, onToggle }) {
  const contact = useOwnezzyContact();
  const [site, setSite] = useState(null);
  const [savedCollapsed, setCollapsed] = useState(readCollapsed);
  const collapsed = savedCollapsed && !floating;

  useEffect(() => {
    getSites({ limit: 1, sort: "name" })
      .then(({ data }) => setSite(data.sites[0] || null))
      .catch(() => setSite(null));
  }, []);

  const toggle = () => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, value ? "0" : "1");
      } catch {
        // private browsing: the guide simply opens again next time
      }
      return !value;
    });
  };

  if (!site) return null;
  const editor = `/admin/sites/${site.id}/edit`;
  const steps = [
    { icon: "✎", title: "Edit your store website", text: "Change banners, text, colours and store details.", to: editor },
    { icon: "＋", title: "Add or update products", text: "Add new products, prices, photos and stock.", to: `${editor}?tab=Products` },
    { icon: "★", title: "Choose featured products", text: "Pick the products shown at the top of your home page.", to: `${editor}?tab=Featured` },
    { icon: "%", title: "Put products on offer", text: "Show discounted products in an Offers section.", to: `${editor}?tab=Offers` },
    { icon: "▣", title: "Manage your orders", text: "See new orders, update their status and add tracking.", to: `/admin/sites/${site.id}/orders` },
    { icon: "↗", title: "Set shipping charges and GST", text: "Delivery fee, free-shipping amount and GST rate.", hash: "#store-shipping" },
    { icon: "₹", title: "Connect Razorpay", text: "Take UPI payments (PhonePe, Google Pay, QR) into your account.", hash: "#store-payment" },
    { icon: "♙", title: "Update your business information", text: "Name, email, contact number and address.", hash: "#store-business" },
    { icon: "◷", title: "Renew your plan", text: "Check your plan and pay before it expires.", tab: "Billing" },
  ];

  return (
    <FloatingPanel floating={floating} open={open} onToggle={onToggle} slot={2} tone="is-info" icon="i" label="How to manage your store">
      <section className={`store-guide ${collapsed ? "is-collapsed" : ""}`} aria-label="How to use your admin panel">
        <button className="store-guide-head" onClick={toggle} aria-expanded={!collapsed}>
          <span className="store-guide-icon" aria-hidden="true">i</span>
          <span className="store-guide-title"><b>How to manage your store</b><small>Quick links to everything you can do here</small></span>
          <span className={`store-guide-chevron ${collapsed ? "" : "is-open"}`} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          </span>
          <span className="store-guide-toggle-label">{collapsed ? "Show" : "Hide"}</span>
        </button>
        {!collapsed && (
          <div className="store-guide-body">
            <a className="store-guide-live" href={site.url} target="_blank" rel="noreferrer">
              <span>Your store is live at</span><b>{site.url.replace(/^https?:\/\//, "")}</b><em>Open ↗</em>
            </a>
            <ul className="store-guide-list">
              {steps.map((step) => {
                const body = <><span className="store-guide-step-icon" aria-hidden="true">{step.icon}</span><span className="store-guide-step-copy"><b>{step.title}</b><small>{step.text}</small><code>{step.to || (step.hash ? `/admin${step.hash}` : `Menu → ${step.tab}`)}</code></span><span className="store-guide-go" aria-hidden="true">→</span></>;
                return (
                  <li key={step.title}>
                    {step.to && <Link to={step.to}>{body}</Link>}
                    {step.hash && <a href={step.hash} onClick={openSettingsSection}>{body}</a>}
                    {step.tab && <button type="button" onClick={() => setTab(step.tab)}>{body}</button>}
                  </li>
                );
              })}
            </ul>
            <p className="store-guide-help">Stuck or have a question? <a href={supportLink(supportMessage(user, "I need help with my store."), contact.whatsapp)} target="_blank" rel="noreferrer">Chat with Ownezzy support on WhatsApp</a>.</p>
          </div>
        )}
      </section>
    </FloatingPanel>
  );
}
